/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import isUndefined from 'lodash.isundefined';
import Sisense from '@ion/core/src/services/Sisense';
import {SisenseState} from '@ion/core/src/services/SisenseUtils';
import {isFalse} from '@ion/core/src/utils/BooleanUtils';
import {globalGroupSelector} from '../../store/selectors/global-group';
import useSisenseSelector from './useSisenseSelector';

/**
 * @internal
 *
 * The `useReportsProcessing` hook determines if the Sisense Reports for the current
 * account have yet to have been processed, in which case, returns `true`, indicating
 * that the user should be informed of that reports are currently being processed for
 * the current account. Otherwise, returns `false`, indicating that the reports are
 * ready for use and the user need not be notified of the processing status.
 */
const useReportsProcessing = (): [boolean, number] => {
  const options     = useSisenseSelector(SisenseState.ServerOptions);
  const globalGroup = useSelector(globalGroupSelector);
  const prefetched  = useRef(false);
  const completed   = useRef(false);
  const pending     = useRef(isUndefined(options));
  const [processingComplete, setProcessingComplete] = useState<boolean>();
  const [trigger, setTrigger] = useState(0);

  /**
   * When the `options` change, we first check if they are defined, in which case if
   * the options have not been prefetched and are pending, we set the status of based
   * on the options, otherwise, we set prefetched to to true, indicating that they
   * need to be re-fetched and re-checked.
   *
   * If the options are not defined, then we set prefetched to `false` and pending
   * to `true`, indicating that once the options are retrieved the status is to be
   * used.
   */
  useEffect(() => {
    if (options) {
      if (pending.current) {
        setProcessingComplete(options.processingComplete);
        setTrigger(prev => prev + 1);
        completed.current  = true;
        pending.current    = false;
      } else {
        prefetched.current = true;
        completed.current  = false;
      }
    } else {
      pending.current    = true;
      completed.current  = false;
      prefetched.current = false;
    }
  }, [options]);

  useEffect(() => {
    if (!pending.current) {
      setProcessingComplete(true);
      setTrigger(prev => prev + 1);
      pending.current = true;
    }
  }, [globalGroup]);

  useEffect(() => {
    if (prefetched.current && !completed.current && options?.processingComplete === false) {
      const getLatestStatus = async() => {
        const status = await Sisense.getReportProcessingStatus();
        if (!isUndefined(status)) {
          setProcessingComplete(status);
          setTrigger(prev => prev + 1);
          completed.current  = true;
          pending.current    = false;
          prefetched.current = false;
        }
      };
      getLatestStatus();
    }
  }, [options]);

  return [isFalse(processingComplete), trigger];
};
export default useReportsProcessing;
